if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
    }

    setupEventListeners() {
      this.variantSelector?.addEventListener('change', this.onVariantChange.bind(this));
      this.addEventListener('change', this.onQuantitySelectorEvent.bind(this));
      this.addEventListener('click', this.onQuantitySelectorEvent.bind(this));
      
      document.getElementById('swiper-script')?.addEventListener('load', this.initSwiper.bind(this));
      document.addEventListener('liquid-ajax-cart:request-end', this.onCartUpdate.bind(this));

      this.setupSizeGuideEvents();
      this.setupSizeGuideKeyboardAccess();
      this.setupLowStockIndicator();
      this.setupViewerCount();
      this.setupNotifyMePopup();
      this.setupReviewSummaryPopup();
      this.setupPillVariantKeyboardAccess();
    }

    syncPillVariantTabOrder() {
      this.querySelectorAll('.product-form__input input.pill-radio').forEach((radio) => {
        radio.tabIndex = -1;

        if (!radio.id) return;

        const label = this.querySelector(`label.pill-label[for="${CSS.escape(radio.id)}"]`);
        if (!label) return;

        label.tabIndex = radio.disabled ? -1 : 0;
      });
    }

    setupPillVariantKeyboardAccess() {
      this.syncPillVariantTabOrder();

      if (this._pillVariantKeyboardBound) return;
      this._pillVariantKeyboardBound = true;

      this.addEventListener('keydown', (event) => {
        const label = event.target.closest('label.pill-label');
        if (!label || !this.contains(label)) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const radioId = label.getAttribute('for');
        if (!radioId) return;

        const radio = this.querySelector(`#${CSS.escape(radioId)}`);
        if (!radio || radio.disabled) return;

        event.preventDefault();
        label.click();
      });

      this._pillVariantObserver = new MutationObserver(() => {
        this.syncPillVariantTabOrder();
      });
      this._pillVariantObserver.observe(this, {
        childList: true,
        subtree: true,
      });
    }

    get isTiloPromotionTemplate() {
      const productTemplateSuffix = String(this.dataset.productTemplate || '').trim().toLowerCase();
      const sectionId = String(this.dataset.section || '').trim().toLowerCase();
      const hasTiloPromotionTemplateSuffix = ['tilo_promotion', 'tilo-redesign', 'tilo_redesign'].includes(productTemplateSuffix);
      const hasTiloSectionId = sectionId.includes('main-product-tilo');
      return hasTiloPromotionTemplateSuffix || hasTiloSectionId || this.dataset.tiloPromotionTemplate === 'true' || document.body.classList.contains('product-template-suffix-tilo_promotion');
    }

    get isTiloPromotionYouthStyleActive() {
      const styleVariantsElement = this.querySelector('style-variants');
      if (!styleVariantsElement) return false;

      const activeStyleVariants = styleVariantsElement.querySelectorAll('.color-variant-single.active');
      if (activeStyleVariants.length === 1) {
        const activeStyleGroup = activeStyleVariants[0].closest('.style-variant-group');
        const activeStyleGroupValue = String(activeStyleGroup?.dataset?.styleGroup || '').trim().toLowerCase();
        if (!activeStyleGroupValue) return false;
        return activeStyleGroupValue === 'youth';
      }

      let currentProductHandle = '';
      const productUrl = String(this.dataset.url || '').trim();
      if (productUrl) {
        try {
          const url = new URL(productUrl, window.location.origin);
          const pathSegments = url.pathname.split('/').filter(Boolean);
          const productsIndex = pathSegments.indexOf('products');
          if (productsIndex !== -1) {
            currentProductHandle = String(pathSegments[productsIndex + 1] || '').trim().toLowerCase();
          }
        } catch (error) {
          const match = productUrl.match(/\/products\/([^/?#]+)/i);
          currentProductHandle = match?.[1] ? String(match[1]).trim().toLowerCase() : '';
        }
      }

      let styleVariantForCurrentProduct = null;
      if (currentProductHandle) {
        styleVariantForCurrentProduct = styleVariantsElement.querySelector(
          `.color-variant-single[data-product-handle="${currentProductHandle}"]`
        );
      }

      const activeStyleVariant =
        styleVariantForCurrentProduct ||
        styleVariantsElement.querySelector('.color-variant-single.active');

      if (!activeStyleVariant) return false;

      const activeStyleGroup = activeStyleVariant.closest('.style-variant-group');
      const activeStyleGroupValue = String(activeStyleGroup?.dataset?.styleGroup || '').trim().toLowerCase();

      if (!activeStyleGroupValue) return false;
      return activeStyleGroupValue === 'youth';
    }

    getInlineQuantitySelectorElement() {
      return this.querySelector(`#quantity-selector-inline-${this.dataset.section}`) || this.querySelector('.product__quantity--inline');
    }

    hasTiloPromotionStyleGroupChoice() {
      const styleVariantsElement = document.querySelector('style-variants');
      if (!styleVariantsElement) return false;

      const styleGroups = Array.from(styleVariantsElement.querySelectorAll('.style-variant-group'))
        .map((group) => String(group?.dataset?.styleGroup || '').trim().toLowerCase())
        .filter(Boolean);

      if (!styleGroups.length) return false;
      return styleGroups.includes('youth') || styleGroups.includes('adult');
    }

    isTiloPromotionMultiSizeActive() {
      return this.isTiloPromotionYouthStyleActive || !this.hasTiloPromotionStyleGroupChoice();
    }

    updateTiloPromotionQuantitySelectorVisibility() {
      if (!this.isTiloPromotionTemplate) return;

      const quantitySelectorElement = this.getInlineQuantitySelectorElement();
      if (!quantitySelectorElement) return;

      const shouldShowQuantitySelector = this.isTiloPromotionMultiSizeActive();
      quantitySelectorElement.hidden = !shouldShowQuantitySelector;
      quantitySelectorElement.style.display = shouldShowQuantitySelector ? '' : 'none';

      const quantityInput = quantitySelectorElement.querySelector('input[type="number"]');
      if (!quantityInput) return;

      const wasYouthActive = this._tiloPromotionLastYouthVisibility === true;

      if (shouldShowQuantitySelector) {
        if (!wasYouthActive) {
          const parsedCurrentValue = parseInt(quantityInput.value, 10);
          if (Number.isNaN(parsedCurrentValue) || parsedCurrentValue < 2) {
            quantityInput.value = '2';
          }
        }
        this._tiloPromotionLastYouthVisibility = true;
        return;
      }

      quantityInput.value = '1';
      this._tiloPromotionLastYouthVisibility = false;
    }

    applySizeGuideTriggerAccessibility(trigger) {
      if (!trigger) return;

      trigger.setAttribute('role', 'button');
      trigger.tabIndex = 0;

      const labelText = trigger.querySelector(':scope > span')?.textContent?.trim() || 'Size Guide';
      trigger.setAttribute('aria-label', `Open ${labelText}`);
    }

    syncSizeGuideTriggerAccessibility() {
      this.querySelectorAll('.size-chart-trigger').forEach((trigger) => {
        this.applySizeGuideTriggerAccessibility(trigger);
      });
    }

    setupSizeGuideKeyboardAccess() {
      this.syncSizeGuideTriggerAccessibility();

      if (this._sizeGuideKeyboardBound) return;
      this._sizeGuideKeyboardBound = true;

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const closeButton = event.target.closest('.size-guide-close');
        if (closeButton && this.contains(closeButton)) {
          event.preventDefault();
          closeButton.click();
          return;
        }

        const trigger = event.target.closest('.size-chart-trigger');
        if (!trigger || !this.contains(trigger)) return;

        event.preventDefault();
        trigger.click();
        requestAnimationFrame(() => {
          const overlayClose = this.querySelector('.size-guide-overlay.active .size-guide-close');
          if (overlayClose) {
            overlayClose.focus();
            return;
          }

          const inner = this.querySelector('.size-guide--inner');
          if (inner?.style.display === 'block') {
            inner.querySelector('.size-guide-close')?.focus();
          }
        });
      });
    }

    setupSizeGuideEvents() {
      const sizeGuideOverlay = this.querySelector('.size-guide-overlay');
      const sizeGuideInner = this.querySelector('.size-guide--inner');
      
      // Prefer modal overlay approach whenever overlay markup exists (PDP + quick-add)
      if (sizeGuideOverlay) {
        const openFromTrigger = (event) => {
          const trigger = event.target.closest('.size-chart-trigger');
          if (!trigger || !this.contains(trigger)) return;
          event.preventDefault();
          sizeGuideOverlay.classList.add('active');
          document.body.style.overflow = 'hidden';
        };

        const closeModal = () => {
          sizeGuideOverlay.classList.remove('active');
          document.body.style.overflow = '';
        };
        
        // Handle size chart trigger clicks
        this.addEventListener('click', openFromTrigger);
        document.addEventListener('click', openFromTrigger);

        // Handle all overlay clicks (outside modal and close button)
        sizeGuideOverlay.addEventListener('click', (event) => {
          // Handle clicks outside modal to close it
          if (event.target === sizeGuideOverlay) {
            closeModal();
            return;
          }
          
          // Handle close button clicks
          const closeBtn = event.target.closest('.size-guide-close');
          if (closeBtn) {
            event.preventDefault();
            event.stopPropagation();
            closeModal();
            return;
          }
        });
        
        // Prevent closing when clicking inside modal content (but allow close button to work)
        if (sizeGuideInner) {
          sizeGuideInner.addEventListener('click', (event) => {
            // Allow close button clicks to propagate to overlay handler
            if (!event.target.closest('.size-guide-close')) {
              event.stopPropagation();
            }
          });
        }
        
        // Close on ESC key
        const escHandler = (event) => {
          if (event.key === 'Escape' && sizeGuideOverlay.classList.contains('active')) {
            closeModal();
          }
        };
        document.addEventListener('keydown', escHandler);
        return;
      }
      
      // Fallback for templates without overlay markup
      if (sizeGuideInner) {
        const openFromTrigger = (event) => {
          const trigger = event.target.closest('.size-chart-trigger');
          if (!trigger || !this.contains(trigger)) return;
          event.preventDefault();
          sizeGuideInner.style.display = 'block';
          document.body.style.overflow = 'hidden';
        };

        // Use event delegation to handle dynamically created elements
        this.addEventListener('click', (event) => {
          // Handle size chart trigger clicks
          if (event.target.matches('.size-chart-trigger') || event.target.closest('.size-chart-trigger')) {
            if (sizeGuideInner) {
              event.preventDefault();
              sizeGuideInner.style.display = 'block';
              document.body.style.overflow = 'hidden';
            }
          }
          
          // Handle size guide close clicks
          if (event.target.matches('.size-guide-close') || event.target.closest('.size-guide-close')) {
            if (sizeGuideInner) {
              sizeGuideInner.style.display = 'none';
              document.body.style.overflow = '';
            }
          }
          
          // Handle clicks on size guide background to close
          if (event.target === sizeGuideInner) {
            sizeGuideInner.style.display = 'none';
            document.body.style.overflow = '';
          }
        });
        document.addEventListener('click', openFromTrigger);

        // Handle clicks outside sizeGuideInner to close it
        document.addEventListener('click', (event) => {
          if (!sizeGuideInner) return;
          
          if (sizeGuideInner.style.display === 'block') {
            const isClickInside = sizeGuideInner.contains(event.target);
            const isTriggerClick = event.target.matches('.size-chart-trigger') || event.target.closest('.size-chart-trigger');
            
            if (!isClickInside && !isTriggerClick) {
              sizeGuideInner.style.display = 'none';
              document.body.style.overflow = '';
            }
          }
        });
      }
    }

    setupLowStockIndicator() {
      this.updateLowStockIndicator();
      this.updatePreOrderProperty();
      
      document.addEventListener('click', (event) => {
        if (event.target.matches('input.pill-radio')) {
          this.updateLowStockIndicator();
          this.updatePreOrderProperty();
        }
      });
    }

    updateLowStockIndicator() {
      const checkedPillRadio = document.querySelector('input.pill-radio:checked');
      const lowStockDiv = document.querySelector('.low-stock-indicator');
      
      if (checkedPillRadio && lowStockDiv) {
        if (checkedPillRadio.getAttribute('data-low-stock') === 'true') {
          lowStockDiv.style.display = 'block';
        } else {
          lowStockDiv.style.display = 'none';
        }
      } else if (lowStockDiv) {
        lowStockDiv.style.display = 'none';
      }
    }

    updatePreOrderProperty(variant) {
      const packInfoInput = document.querySelector('#pack-info-property');
      
      if (!packInfoInput) return;
      
      // If no variant passed, try to get it from the current selection
      if (!variant) {
        const checkedPillRadio = document.querySelector('input.pill-radio:checked');
        const isPreOrder = checkedPillRadio && checkedPillRadio.hasAttribute('Pre-order-true');
        
        if (!isPreOrder) {
          packInfoInput.disabled = true;
          packInfoInput.value = '';
          packInfoInput.removeAttribute('data-preorder-delivery-date');
          return;
        }
      }
      
      let deliveryDate = '';
      
      // Check if atc-text span has variant delivery date (highest priority)
      const atcTextSpan = document.querySelector('.atc-text');
      if (atcTextSpan && atcTextSpan.hasAttribute('variant-pre-order-delivery-date')) {
        deliveryDate = atcTextSpan.getAttribute('variant-pre-order-delivery-date');
      }
      // Fallback to input's own attribute
      else if (packInfoInput.hasAttribute('data-preorder-delivery-date')) {
        deliveryDate = packInfoInput.getAttribute('data-preorder-delivery-date');
      }
      
      if (deliveryDate) {
        packInfoInput.value = 'Estimated delivery date is ' + deliveryDate + '.';
        packInfoInput.disabled = false;
      } else {
        packInfoInput.removeAttribute('data-preorder-delivery-date');
        packInfoInput.value = 'Not Available';
        packInfoInput.disabled = true;
      }
    }

    setupViewerCount() {
      if (!this.productId) return;
      
      // Generate a somewhat random but stable number between 3-15 based on product ID
      const baseNumber = ((parseInt(this.productId) % 10) + 3);
      const randomVariation = Math.floor(Math.random() * 5);
      const viewers = baseNumber + randomVariation;

      const viewerElement = this.querySelector('#current-viewers');
      if (viewerElement) {
        viewerElement.textContent = viewers;
      }
    }

    setupNotifyMePopup() {
      this.addEventListener('click', (event) => {
        const notifyTrigger = event.target.closest('.notify-me-popup-trigger');
        if (!notifyTrigger) return;
        event.preventDefault();

        const closestContainer = notifyTrigger.parentElement;
        const notifyMePopup =
          closestContainer?.querySelector('notify-me-popup') ||
          this.querySelector('notify-me-popup') ||
          document.querySelector('notify-me-popup');
        if (!notifyMePopup) return;

        if (typeof notifyMePopup.show === 'function') {
          notifyMePopup.show();
        } else {
          notifyMePopup.style.display = 'block';
          document.body.style.overflow = 'hidden';
        }
      });
    }

    setupReviewSummaryPopup() {
      // Use event delegation on the product-info element (doesn't get replaced)
      this.addEventListener('click', (event) => {
        // Toggle tooltip on link click
        if (event.target.matches('.reveiws_summary') || event.target.closest('.reveiws_summary')) {
          event.preventDefault();
          const tooltip = event.target.closest('.review-summary-wrapper')?.querySelector('.review-summary--tooltip');
          if (tooltip) {
            tooltip.style.display = tooltip.style.display === 'none' ? 'block' : 'none';
          }
        }
        
        // Close tooltip when clicking view button
        if (event.target.matches('.review-summary--view-btn') || event.target.closest('.review-summary--view-btn')) {
          const tooltip = event.target.closest('.review-summary-wrapper')?.querySelector('.review-summary--tooltip');
          if (tooltip) {
            tooltip.style.display = 'none';
          }
        }
        
        // Close tooltip when clicking close button (if it exists)
        const closeButton = event.target.closest('.review-summary--header .close-button');
        if (closeButton) {
          const tooltip = closeButton.closest('.review-summary--tooltip');
          if (tooltip) {
            tooltip.style.display = 'none';
          }
        }
      });

      // Close tooltip when clicking outside
      document.addEventListener('click', (event) => {
        // Don't cache the tooltip - query it each time
        const allTooltips = this.querySelectorAll('.review-summary--tooltip');
        allTooltips.forEach(tooltip => {
          if (tooltip && !event.target.closest('.review-summary-wrapper') && tooltip.style.display === 'block') {
            tooltip.style.display = 'none';
          }
        });
      });
    }

    connectedCallback() {
      this.setupEventListeners();
      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
      }

      setTimeout(() => {
        this.updateAddToCartVisibilityOnLoad();
      }, 100);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.updateAddToCartVisibilityOnLoad();
        });
      } else {
        setTimeout(() => {
          this.updateAddToCartVisibilityOnLoad();
        }, 200);
      }

      window.Shop = window.Shop || {};
      window.Shop.productInfoEl = this;

      this.setupVariantIdSyncOnSubmit();
      this.setupTiloPromotionMultiSize();
    }

    disconnectedCallback() {
      if (this._pillVariantObserver) {
        this._pillVariantObserver.disconnect();
        this._pillVariantObserver = null;
      }

      if (this._tiloPromotionForm && this._tiloPromotionSubmitHandler) {
        this._tiloPromotionForm.removeEventListener('submit', this._tiloPromotionSubmitHandler, true);
      }
      this._tiloPromotionForm = null;

      if (this._variantSyncForm && this._variantSyncSubmitHandler) {
        this._variantSyncForm.removeEventListener('submit', this._variantSyncSubmitHandler, true);
      }
      this._variantSyncForm = null;
    }

    initSwiper() {
      const isInQuickAddModal = this.closest('quick-add-modal') !== null;
      const isGameDaySocksTemplate = document.body.classList.contains('product-template-suffix-game-day-socks-new');
      const forceSingleSlideView = isInQuickAddModal || isGameDaySocksTemplate;
      const sliderElement = this.querySelector('.gallery-slider');
      if (!sliderElement) return;
      
      // Use scoped navigation elements so each modal controls its own slider
      const navPrevSelector = isInQuickAddModal ? '.swiper-button-modal.swiper-button-prev' : '.swiper-button-pdp.swiper-button-prev';
      const navNextSelector = isInQuickAddModal ? '.swiper-button-modal.swiper-button-next' : '.swiper-button-pdp.swiper-button-next';
      const navPrevElement = this.querySelector(navPrevSelector);
      const navNextElement = this.querySelector(navNextSelector);
      const paginationElement = this.querySelector('.swiper-pagination');

      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
      }

      this.swiper = new Swiper(sliderElement, {
        autoHeight: true,
        direction: 'horizontal',
        loop: false,
        slidesPerView: 1,
        spaceBetween: 4,
        ...(!forceSingleSlideView && {
          breakpoints: {
            750: {
              slidesPerView: 2,
            },
          },
        }),
        ...(paginationElement && {
          pagination: {
            el: paginationElement,
          },
        }),
        navigation: {
          prevEl: navPrevElement,
          nextEl: navNextElement,
        },
        on: {
          slideChange: () => {
            this.updateThumbnailActive();
          },
        }
      });
      
      this.initThumbnails();
    }

    initThumbnails() {
      const thumbnails = this.querySelectorAll('.thumbnail-item');
      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          this.swiper?.slideTo(index);
        });
      });
      this.updateThumbnailActive();
    }

    initThumbnailClicks() {
      // This method is no longer needed
    }

    updateThumbnailActive() {
      const thumbnails = this.querySelectorAll('.thumbnail-item');
      thumbnails.forEach((thumb, index) => {
        thumb.classList.toggle('active', index === this.swiper?.activeIndex);
      });
    }

    onCartUpdate(e) {
      if (!window.location.pathname.includes('/products/')) return;
      const { requestState } = e.detail;
      // If the "add to cart" request is successful
      if (requestState.requestType === 'add' && requestState.responseData?.ok) {
        // Add the CSS class to the "body" tag
        document.body.classList.add('js-show-ajax-cart');
        // dispatch a custom event
        document.dispatchEvent(
          new CustomEvent('item-added-to-cart', {
            detail: requestState?.responseData?.body,
          })
        );
      }
    }

    get variantSelector() {
      return this.querySelector('variant-selector');
    }

    get quantitySelector() {
      return this.querySelector('quantity-selector');
    }

    get selectedOptionValues() {
      if (this.variantSelector.dataset.pickerType === 'dropdown') {
        const list = Array.from(this.variantSelector.querySelectorAll('select')).map(
          (select) => select.options[select.selectedIndex].dataset.optionValueId
        );
        return list;
      } else {
        const list = Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
        return list;
      }
    }

    getSelectedVariant(html) {
      const selectedVariant = html.querySelector('[data-selected-variant]')?.innerHTML;
      if (!selectedVariant) return null;

      try {
        return JSON.parse(selectedVariant);
      } catch {
        return null;
      }
    }

    resolveQuickAddProductUrl(e) {
      const eventProductUrl = e.target?.dataset?.productUrl;
      if (eventProductUrl) return eventProductUrl;

      const topLevelActiveColor = this.querySelector(':scope > color-variants .color-variant-single.active[data-product-url]');
      if (topLevelActiveColor?.dataset?.productUrl) return topLevelActiveColor.dataset.productUrl;

      const attachedActiveColor = this.querySelector(
        'style-variants .color-variant-single.active .colors-attached-with-style color-variants .color-variant-single.active[data-product-url]'
      );
      if (attachedActiveColor?.dataset?.productUrl) return attachedActiveColor.dataset.productUrl;

      const visibleActiveColor = Array.from(
        this.querySelectorAll('color-variants .color-variant-single.active[data-product-url]')
      ).find((colorItem) => {
        const attachedWrapper = colorItem.closest('.colors-attached-with-style');
        if (!attachedWrapper) return true;
        const parentStyle = attachedWrapper.closest('style-variants .color-variant-single');
        return !!parentStyle?.classList.contains('active');
      });

      if (visibleActiveColor?.dataset?.productUrl) return visibleActiveColor.dataset.productUrl;
      return this.dataset.url;
    }

    getSelectedOptionTextValues() {
      if (!this.variantSelector) return [];
      if (this.variantSelector.dataset.pickerType === 'dropdown') {
        return Array.from(this.variantSelector.querySelectorAll('select')).map(
          (select) => select.options[select.selectedIndex]?.value
        ).filter(Boolean);
      }

      return Array.from(this.variantSelector.querySelectorAll('fieldset input:checked')).map(
        (input) => input.value
      ).filter(Boolean);
    }

    getQuickAddProductDataByUrl(productUrl) {
      const escapedUrl = (window.CSS && typeof window.CSS.escape === 'function')
        ? window.CSS.escape(productUrl)
        : productUrl.replace(/"/g, '\\"');

      const topLevelNode = this.querySelector(
        `:scope > color-variants .color-variant-single[data-product-url="${escapedUrl}"][data-product-json]`
      );
      if (topLevelNode?.dataset?.productJson) return topLevelNode.dataset.productJson;

      const attachedNode = this.querySelector(
        `style-variants .color-variant-single.active .colors-attached-with-style color-variants .color-variant-single[data-product-url="${escapedUrl}"][data-product-json]`
      );
      if (attachedNode?.dataset?.productJson) return attachedNode.dataset.productJson;

      const anyNode = this.querySelector(
        `.color-variant-single[data-product-url="${escapedUrl}"][data-product-json]`
      );
      return anyNode?.dataset?.productJson || null;
    }

    resolveQuickAddVariantFallback(productUrl) {
      const productDataString = this.getQuickAddProductDataByUrl(productUrl);
      if (!productDataString) return null;

      let productData;
      try {
        productData = JSON.parse(productDataString);
      } catch {
        return null;
      }

      const selectedOptionValues = this.getSelectedOptionTextValues();
      if (!selectedOptionValues.length) return productData?.variants?.[0] || null;

      const matchedVariant = productData.variants?.find((variant) => {
        const variantOptions = Array.isArray(variant.options) && variant.options.length
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);
        if (!variantOptions.length) return false;

        return selectedOptionValues.every((value, index) => variantOptions[index] === value);
      });

      return matchedVariant || productData?.variants?.[0] || null;
    }

    getExternalVariantIdFromEventTarget(target) {
      if (!target) return 0;

      if (target.matches('select')) {
        const selectedOption = target.options?.[target.selectedIndex];
        const rawId = selectedOption?.dataset?.externalVariantId;
        return rawId ? parseInt(rawId, 10) || 0 : 0;
      }

      const rawId = target.dataset?.externalVariantId;
      return rawId ? parseInt(rawId, 10) || 0 : 0;
    }

    onVariantChange(e) {
      const externalVariantId = this.getExternalVariantIdFromEventTarget(e.target);
      if (externalVariantId > 0) {
        this.updateVariantInputs(externalVariantId);
        this.updatePreOrderProperty();
        return;
      }

      const selectedQuantity = this.getCurrentQuantityValue();

      const isInQuickAddModal = this.closest('quick-add-modal') !== null;
      
      let hasDifferentProductUrl = false;
      let productUrl = this.dataset.url;
      
      if (isInQuickAddModal) {
        productUrl = this.resolveQuickAddProductUrl(e);
      } else {
        hasDifferentProductUrl = e.target?.dataset?.productUrl ? (e.target?.dataset?.productUrl !== this.dataset.url) : false;
        productUrl = e.target?.dataset?.productUrl || this.dataset.url;
      }
      
      this.updatePreOrderProperty();
      this.renderSection(hasDifferentProductUrl, productUrl, selectedQuantity);
    }

    onQuantitySelectorEvent(e) {
      const quantitySelector = e.target.closest('quantity-selector');
      if (!quantitySelector || !this.contains(quantitySelector)) return;

      const quantityInput = quantitySelector.querySelector('input[type="number"]');
      if (!quantityInput) return;

      if (e.type === 'click') {
        const clickedButton = e.target.closest('button[name="plus"], button[name="minus"]');
        if (!clickedButton) return;
        e.preventDefault();
      }

      const minRawValue = parseInt(quantityInput.getAttribute('min'), 10);
      const maxRawValue = parseInt(quantityInput.getAttribute('max'), 10);
      const stepRawValue = parseInt(quantityInput.getAttribute('step'), 10);

      const minValue = Number.isNaN(minRawValue) ? 1 : minRawValue;
      const maxValue = Number.isNaN(maxRawValue) ? Infinity : maxRawValue;
      const stepValue = Number.isNaN(stepRawValue) || stepRawValue < 1 ? 1 : stepRawValue;

      const currentRawValue = parseInt(quantityInput.value, 10);
      const currentValue = Number.isNaN(currentRawValue) ? minValue : currentRawValue;

      const triggerButton = e.target.closest('button');
      const triggerName = triggerButton?.name || e.target?.name;

      if (triggerName === 'minus' && currentValue > minValue) {
        quantityInput.value = String(Math.max(minValue, currentValue - stepValue));
      } else if (triggerName === 'plus' && currentValue < maxValue) {
        quantityInput.value = String(Math.min(maxValue, currentValue + stepValue));
      } else if (e.type === 'change') {
        if (currentValue < minValue) {
          quantityInput.value = String(minValue);
        } else if (currentValue > maxValue) {
          quantityInput.value = String(maxValue);
        }
      }

      if (e.type === 'click') {
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncTiloPromotionSizeSelectors();
    }

    updateMedia(variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;
      var index = this.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`).dataset.mediaIndex;
      this.swiper?.slideTo(index);
    }

    updateURL(variantId) {
      // this.querySelector('share-button')?.updateUrl(
      //   `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
      // );
      if (!window.location.pathname.includes('/products/')) return;
      window.history.replaceState({}, '', `${this.dataset.url}${variantId ? `?variant=${variantId}` : ''}`);
    }

    updateDiscountPricing(variant) {
      if (!variant || typeof discount_applied_25 === 'undefined') return;
      
      // Check if any discount is applied
      if (discount_applied_25 || discount_applied_30 || discount_applied_40 || collection_custom || site_wide) {
        const priceContainer = this.querySelector('.price');
        
        if (priceContainer && typeof discount_value !== 'undefined') {
          // Calculate discounted price
          const discountedPrice = Math.round(variant.price * discount_value);
          
          // Update current price elements
          const regularPriceElements = priceContainer.querySelectorAll('.price-item--regular');
          const salePriceElements = priceContainer.querySelectorAll('.price-item--sale');
          
          // Update the displayed prices
          regularPriceElements.forEach(element => {
            if (!element.closest('s')) { // Only update non-crossed out prices
              element.innerHTML = this.formatMoney(discountedPrice);
            }
          });
          
          salePriceElements.forEach(element => {
            element.innerHTML = this.formatMoney(discountedPrice);
          });
          
          // Show compare price (original price crossed out)
          const comparePriceElements = priceContainer.querySelectorAll('s .price-item--regular');
          comparePriceElements.forEach(element => {
            element.innerHTML = this.formatMoney(variant.price);
          });
          
          // Add sale class to price container
          priceContainer.classList.add('price--on-sale');
        }
      }
    }

    updateAddToCartVisibility(variant) {
      const addToCartForm = this.querySelector('ajax-cart-product-form');
      const notifyMeTrigger = this.querySelector('.notify-me-popup-trigger');
      const addToCartButton = this.querySelector('button[name="add"]');
      
      if (!addToCartForm || !notifyMeTrigger) return;
      
      const checkedPillRadio = this.querySelector('input.pill-radio:checked');
      const isPillMarkedUnavailable = checkedPillRadio
        ? checkedPillRadio.classList.contains('disabled') || checkedPillRadio.hasAttribute('disabled')
        : false;
      const isVariantAvailableFromData = typeof variant?.available === 'boolean'
        ? variant.available
        : !addToCartButton?.disabled;
      const isVariantAvailable = !isPillMarkedUnavailable && isVariantAvailableFromData;
      
      if (isVariantAvailable) {
        addToCartForm.style.display = 'block';
        notifyMeTrigger.style.display = 'none';
      } else {
        addToCartForm.style.display = 'none';
        notifyMeTrigger.style.display = 'block';
      }
    }

    updateAddToCartVisibilityOnLoad() {
      const variantInput = this.querySelector('input[name="id"]');
      if (!variantInput) return;
    
      const currentVariantId = variantInput.value;
      if (!currentVariantId) return;
    
      let productData =
        this.querySelector('[data-product-json]') ||
        this.querySelector('[data-product]') ||
        document.querySelector('[data-product-json]') ||
        document.querySelector('[data-product]');
    
      if (!productData) {
        this.updateAddToCartVisibilityOnLoadFallback();
        return;
      }
    
      let rawJson = '';
      if (productData.hasAttribute('data-product-json')) {
        rawJson = productData.getAttribute('data-product-json')?.trim() || '';
      } else if (productData.tagName === 'SCRIPT') {
        rawJson = productData.textContent?.trim() || '';
      } else {
        rawJson = productData.textContent?.trim() || '';
      }
    
      if (!rawJson) {
        this.updateAddToCartVisibilityOnLoadFallback();
        return;
      }
    
      let product;
    
      try {
        product = JSON.parse(rawJson);
      } catch (error) {
        console.error('Invalid product JSON:', error);
        this.updateAddToCartVisibilityOnLoadFallback();
        return;
      }
    
      const variant = product.variants?.find(
        v => v.id.toString() === currentVariantId.toString()
      );
    
      if (variant) {
        this.updateAddToCartVisibility(variant);
        this.updatePreOrderProperty(variant);
      } else {
        this.updateAddToCartVisibilityOnLoadFallback();
      }
    }

    updateAddToCartVisibilityOnLoadFallback() {
      const addToCartButton = this.querySelector('button[name="add"]');
      const addToCartForm = this.querySelector('ajax-cart-product-form');
      const notifyMeTrigger = this.querySelector('.notify-me-popup-trigger');
      
      if (!addToCartButton || !addToCartForm || !notifyMeTrigger) return;
      
      const isVariantAvailable = !addToCartButton.disabled;
      
      if (isVariantAvailable) {
        addToCartForm.style.display = 'block';
        notifyMeTrigger.style.display = 'none';
      } else {
        addToCartForm.style.display = 'none';
        notifyMeTrigger.style.display = 'block';
      }
    }

    formatMoney(cents) {
      // Use Shopify's money formatting if available, otherwise fallback to basic formatting
      if (typeof window.moneyFormat !== 'undefined' && window.moneyFormat) {
        return window.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2)).replace('{{amount_no_decimals}}', Math.round(cents / 100));
      } else if (typeof current_currency !== 'undefined') {
        return `${current_currency}${(cents / 100).toFixed(2)}`;
      } else {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    updateSourceFromDestination = (html, id) => {
      const source = html.getElementById(`${id}`);
      const destination = this.querySelector(`#${id}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
      }
    };

    updateVariantInputs(variantId) {
      this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach(
        (productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
        }
      );
    }

    setupVariantIdSyncOnSubmit() {
      const form = this.querySelector('ajax-cart-product-form form[action*="/cart/add"]');
      if (!form) return;

      if (this._variantSyncForm && this._variantSyncSubmitHandler && this._variantSyncForm !== form) {
        this._variantSyncForm.removeEventListener('submit', this._variantSyncSubmitHandler, true);
      }

      if (!this._variantSyncSubmitHandler) {
        this._variantSyncSubmitHandler = this.onProductFormSubmit.bind(this);
      }

      if (this._variantSyncForm !== form) {
        this._variantSyncForm = form;
        form.addEventListener('submit', this._variantSyncSubmitHandler, true);
      }
    }

    onProductFormSubmit(event) {
      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) return;

      const resolvedVariantId = this.getCurrentSelectedVariantId();
      if (!resolvedVariantId) return;

      const variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;

      variantInput.value = String(resolvedVariantId);
    }

    getCurrentQuantityValue() {
      const quantityInput = this.quantitySelector?.querySelector('input[type="number"]');
      if (!quantityInput) return 1;
      const rawValue = parseInt(quantityInput.value, 10);
      if (Number.isNaN(rawValue)) return 1;
      return Math.max(1, rawValue);
    }

    applyQuantityValue(quantity) {
      const quantityInput = this.quantitySelector?.querySelector('input[type="number"]');
      if (!quantityInput) return;

      const minValue = parseInt(quantityInput.getAttribute('min')) || 1;
      const rawMaxValue = parseInt(quantityInput.getAttribute('max'), 10);
      const maxValue = Number.isNaN(rawMaxValue) ? Infinity : rawMaxValue;
      const nextValue = Math.min(maxValue, Math.max(minValue, quantity || minValue));
      quantityInput.value = nextValue;
      this.syncTiloPromotionSizeSelectors();
    }

    setupTiloPromotionMultiSize() {
      if (!this.isTiloPromotionTemplate) return;

      const form = this.querySelector('ajax-cart-product-form form[action*="/cart/add"]');
      if (!form) return;

      if (this._tiloPromotionForm && this._tiloPromotionSubmitHandler && this._tiloPromotionForm !== form) {
        this._tiloPromotionForm.removeEventListener('submit', this._tiloPromotionSubmitHandler, true);
      }

      if (!this._tiloPromotionSubmitHandler) {
        this._tiloPromotionSubmitHandler = this.onTiloPromotionSubmit.bind(this);
      }

      if (this._tiloPromotionForm !== form) {
        this._tiloPromotionForm = form;
        form.addEventListener('submit', this._tiloPromotionSubmitHandler, true);
      }

      const cloneContainer = this.querySelector('[data-tilo-promotion-size-groups]');
      if (!this.isTiloPromotionMultiSizeActive() && cloneContainer) {
        cloneContainer.innerHTML = '';
        cloneContainer.hidden = true;
        cloneContainer.style.display = 'none';
      }

      this.syncTiloPromotionSizeSelectors();
    }

    syncTiloPromotionSizeSelectors() {
      if (!this.isTiloPromotionTemplate) return;

      const variantSelector = this.variantSelector;
      if (!variantSelector) return;

      const quantity = this.getCurrentQuantityValue();
      const additionalSelectors = Math.max(0, quantity - 1);

      let cloneContainer = this.querySelector('[data-tilo-promotion-size-groups]');
      if (!cloneContainer) {
        cloneContainer = document.createElement('div');
        cloneContainer.setAttribute('data-tilo-promotion-size-groups', '');
        cloneContainer.className = 'tilo-promotion-size-groups';
        variantSelector.insertAdjacentElement('afterend', cloneContainer);
      }

      this.updateTiloPromotionQuantitySelectorVisibility();

      if (!this.isTiloPromotionMultiSizeActive()) {
        cloneContainer.innerHTML = '';
        cloneContainer.hidden = true;
        cloneContainer.style.display = 'none';
        return;
      }

      cloneContainer.hidden = false;
      cloneContainer.style.display = '';

      if (additionalSelectors === 0) {
        cloneContainer.innerHTML = '';
        cloneContainer.hidden = true;
        cloneContainer.style.display = 'none';
        return;
      }

      const pickerType = variantSelector.dataset.pickerType;
      if (pickerType === 'dropdown') {
        this.syncTiloPromotionDropdownSelectors(variantSelector, cloneContainer, additionalSelectors);
        return;
      }

      this.syncTiloPromotionRadioSelectors(variantSelector, cloneContainer, additionalSelectors);
    }

    syncTiloPromotionRadioSelectors(variantSelector, cloneContainer, additionalSelectors) {
      const sourceFieldset = variantSelector.querySelector('.variant-select__button .product-form__input');
      if (!sourceFieldset) {
        cloneContainer.innerHTML = '';
        return;
      }

      const sourceCheckedInput = sourceFieldset.querySelector('input[type="radio"]:checked');
      const sourceCheckedValue = sourceCheckedInput?.value || '';
      const previousSelectionsByIndex = new Map();
      const existingCloneFieldsets = cloneContainer.querySelectorAll('fieldset.product-form__input[data-tilo-clone-index]');
      for (const existingCloneFieldset of existingCloneFieldsets) {
        const cloneIndex = existingCloneFieldset.getAttribute('data-tilo-clone-index');
        if (!cloneIndex) continue;
        const checkedInput = existingCloneFieldset.querySelector('input[type="radio"]:checked');
        const selectedValue = checkedInput?.value;
        if (!selectedValue) continue;
        previousSelectionsByIndex.set(cloneIndex, selectedValue);
      }

      cloneContainer.innerHTML = '';

      for (let index = 2; index <= additionalSelectors + 1; index += 1) {
        const clonedFieldset = sourceFieldset.cloneNode(true);
        clonedFieldset.setAttribute('data-tilo-clone-index', String(index));
        const preservedSelectedValue = previousSelectionsByIndex.get(String(index)) || sourceCheckedValue;

        const clonedLegend = clonedFieldset.querySelector('legend');
        if (clonedLegend) {
          clonedLegend.textContent = `Select a size for TILO #${index}`;
        }

        const clonedPriceElement = this.buildTiloPromotionClonedPriceElement();
        if (clonedPriceElement && clonedLegend) {
          clonedLegend.insertAdjacentElement('afterend', clonedPriceElement);
        }

        const clonedSizeGuideTrigger = clonedFieldset.querySelector('.size-chart-trigger');
        if (clonedSizeGuideTrigger) {
          clonedSizeGuideTrigger.remove();
        }

        const clonedInputs = clonedFieldset.querySelectorAll('input[type="radio"]');
        let hasCheckedInput = false;
        for (const input of clonedInputs) {
          const originalId = input.id || `tilo-option-${index}`;
          const nextId = `${originalId}-clone-${index}`;
          input.id = nextId;
          input.name = `${input.name || 'Option'}-clone-${index}`;
          input.checked = input.value === preservedSelectedValue;
          if (input.checked) {
            hasCheckedInput = true;
          }
        }

        if (!hasCheckedInput) {
          const firstEnabledInput = clonedFieldset.querySelector('input[type="radio"]:not([disabled])');
          if (firstEnabledInput) {
            firstEnabledInput.checked = true;
          }
        }

        const clonedLabels = clonedFieldset.querySelectorAll('label[for]');
        for (const label of clonedLabels) {
          const originalFor = label.getAttribute('for');
          if (!originalFor) continue;
          label.setAttribute('for', `${originalFor}-clone-${index}`);
        }

        cloneContainer.appendChild(clonedFieldset);
      }
    }

    syncTiloPromotionDropdownSelectors(variantSelector, cloneContainer, additionalSelectors) {
      const sourceFieldset = variantSelector.querySelector('.variant-block__dropdown fieldset.product-form__input');
      const sourceSelect = sourceFieldset?.querySelector('select');
      if (!sourceFieldset || !sourceSelect) {
        cloneContainer.innerHTML = '';
        return;
      }

      const previousSelectionsByIndex = new Map();
      const existingGroups = cloneContainer.querySelectorAll('.tilo-promotion-size-group[data-tilo-clone-index]');
      for (const existingGroup of existingGroups) {
        const cloneIndex = existingGroup.getAttribute('data-tilo-clone-index');
        if (!cloneIndex) continue;
        const selectedOption = existingGroup.querySelector('select option:checked');
        const selectedValue = selectedOption?.value;
        if (!selectedValue) continue;
        previousSelectionsByIndex.set(cloneIndex, selectedValue);
      }

      cloneContainer.innerHTML = '';

      for (let index = 2; index <= additionalSelectors + 1; index += 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'tilo-promotion-size-group';
        wrapper.setAttribute('data-tilo-clone-index', String(index));
        const preservedSelectedValue = previousSelectionsByIndex.get(String(index));

        const label = document.createElement('label');
        label.className = 'option__label';
        label.textContent = `Select a size for TILO #${index}`;

        const clonedPriceElement = this.buildTiloPromotionClonedPriceElement();

        const fieldset = sourceFieldset.cloneNode(true);
        const select = fieldset.querySelector('select');
        if (select) {
          const selectId = `${select.id || 'Option'}-clone-${index}`;
          select.id = selectId;
          select.name = `${select.name || 'options[Size]'}-clone-${index}`;
          if (preservedSelectedValue) {
            const preservedOption = Array.from(select.options).find((option) => option.value === preservedSelectedValue);
            if (preservedOption) {
              select.value = preservedSelectedValue;
            } else {
              select.selectedIndex = sourceSelect.selectedIndex;
            }
          } else {
            select.selectedIndex = sourceSelect.selectedIndex;
          }
          label.setAttribute('for', selectId);
        }

        wrapper.appendChild(label);
        if (clonedPriceElement) {
          wrapper.appendChild(clonedPriceElement);
        }
        wrapper.appendChild(fieldset);
        cloneContainer.appendChild(wrapper);
      }
    }

    buildTiloPromotionClonedPriceElement() {
      const sourcePriceElement = this.querySelector('.product__price');
      if (!sourcePriceElement) return null;

      const clonedPriceElement = sourcePriceElement.cloneNode(true);
      clonedPriceElement.removeAttribute('id');

      const elementsWithIds = clonedPriceElement.querySelectorAll('[id]');
      for (const element of elementsWithIds) {
        element.removeAttribute('id');
      }

      const promoPriceWrapper = document.createElement('div');
      promoPriceWrapper.className = 'promo-price-main-wrapper';
      promoPriceWrapper.appendChild(clonedPriceElement);

      return promoPriceWrapper;
    }

    getCurrentProductDataFromSwatch() {
      const activeColorVariant = document.querySelector('color-variants .color-variant-single.active');
      const activeStyleVariant = document.querySelector('style-variants .color-variant-single.active');
      const activeVariant = activeColorVariant || activeStyleVariant;
      const productDataString = activeVariant?.dataset?.productJson;
      if (!productDataString) return null;

      try {
        return JSON.parse(productDataString);
      } catch (error) {
        console.error('Unable to parse active product data:', error);
        return null;
      }
    }

    getEmbeddedProductData() {
      const productDataElement =
        this.querySelector('[data-product-json]') ||
        this.querySelector('[data-product]') ||
        document.querySelector('[data-product-json]') ||
        document.querySelector('[data-product]');

      if (!productDataElement) return null;

      const rawJson = productDataElement.getAttribute('data-product-json')?.trim() || productDataElement.textContent?.trim() || '';
      if (!rawJson) return null;

      try {
        return JSON.parse(rawJson);
      } catch (error) {
        console.error('Unable to parse embedded product data:', error);
        return null;
      }
    }

    getCurrentSelectedVariantId() {
      const variantSelector = this.variantSelector;
      if (!variantSelector) return null;

      const selectedControls = [];
      if (variantSelector.dataset.pickerType === 'dropdown') {
        const selects = variantSelector.querySelectorAll('select');
        for (const select of selects) {
          const selectedOption = select.options?.[select.selectedIndex];
          if (!selectedOption) continue;
          selectedControls.push(selectedOption);
        }
      } else {
        const checkedInputs = variantSelector.querySelectorAll('fieldset input[type="radio"]:checked');
        for (const checkedInput of checkedInputs) {
          selectedControls.push(checkedInput);
        }
      }

      for (const control of selectedControls) {
        const resolvedId = this.resolveTiloPromotionVariantId(control.value, control, null);
        if (resolvedId) return resolvedId;
      }

      const productData = this.getCurrentProductDataFromSwatch() || this.getEmbeddedProductData();
      if (!productData?.variants?.length) return null;

      const selectedValues = selectedControls
        .map((control) => String(control.value || '').trim())
        .filter((value) => !!value);
      if (!selectedValues.length) return null;

      for (const variant of productData.variants) {
        if (!variant?.id || variant?.hide_from_pdp === true) continue;
        const variantOptions = [variant.option1, variant.option2, variant.option3]
          .map((optionValue) => String(optionValue || '').trim())
          .filter((optionValue) => !!optionValue);

        const isSelectedCombinationMatch = selectedValues.every((selectedValue) => variantOptions.includes(selectedValue));
        if (isSelectedCombinationMatch) {
          return variant.id;
        }
      }

      return this.resolveTiloPromotionVariantId(selectedValues[0], null, productData);
    }

    async getTiloPromotionProductData() {
      const swatchData = this.getCurrentProductDataFromSwatch();
      if (swatchData?.variants?.length) return swatchData;

      const embeddedData = this.getEmbeddedProductData();
      if (embeddedData?.variants?.length) return embeddedData;

      const productUrl = this.dataset.url;
      if (!productUrl) return null;

      try {
        const response = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
        if (!response.ok) return null;
        const productData = await response.json();
        return productData?.variants?.length ? productData : null;
      } catch (error) {
        console.error('Unable to fetch product JSON for tilo promotion:', error);
        return null;
      }
    }

    resolveTiloPromotionVariantId(selectedValue, selectedInputOrOption, productData) {
      const rawVariantId = selectedInputOrOption?.dataset?.variantId;
      if (rawVariantId) {
        const parsedVariantId = parseInt(rawVariantId, 10);
        if (!Number.isNaN(parsedVariantId) && parsedVariantId > 0) {
          return parsedVariantId;
        }
      }

      const externalVariantId = selectedInputOrOption?.dataset?.externalVariantId;
      if (externalVariantId) {
        const parsedExternalVariantId = parseInt(externalVariantId, 10);
        if (!Number.isNaN(parsedExternalVariantId) && parsedExternalVariantId > 0) {
          return parsedExternalVariantId;
        }
      }

      if (!productData?.variants?.length) return null;

      for (const variant of productData.variants) {
        if (variant?.hide_from_pdp === true) continue;

        const variantOptions = [variant.option1, variant.option2, variant.option3]
          .map((optionValue) => String(optionValue || '').trim())
          .filter((optionValue) => !!optionValue);

        if (variantOptions.includes(String(selectedValue || '').trim())) {
          return variant.id || null;
        }
      }

      return null;
    }

    getTiloPromotionSelectedVariantIds(form, productData) {
      const variantIds = [];
      const resolvedBaseVariantId = this.getCurrentSelectedVariantId();
      if (resolvedBaseVariantId) {
        variantIds.push(resolvedBaseVariantId);
      }

      const baseVariantInput = form.querySelector('input[name="id"]');
      const baseVariantId = parseInt(baseVariantInput?.value || '', 10);
      if (!resolvedBaseVariantId && !Number.isNaN(baseVariantId) && baseVariantId > 0) {
        variantIds.push(baseVariantId);
      }

      const cloneContainer = this.querySelector('[data-tilo-promotion-size-groups]');
      if (!cloneContainer) return variantIds;

      const cloneFieldsets = cloneContainer.querySelectorAll('fieldset.product-form__input');
      for (const cloneFieldset of cloneFieldsets) {
        const checkedInput = cloneFieldset.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          const resolvedId = this.resolveTiloPromotionVariantId(checkedInput.value, checkedInput, productData);
          if (resolvedId) {
            variantIds.push(resolvedId);
            continue;
          }
        }

        const selectedOption = cloneFieldset.querySelector('select option:checked');
        if (selectedOption) {
          const resolvedId = this.resolveTiloPromotionVariantId(selectedOption.value, selectedOption, productData);
          if (resolvedId) {
            variantIds.push(resolvedId);
          }
        }
      }

      return variantIds;
    }

    async addTiloPromotionItems(items) {
      if (!Array.isArray(items) || !items.length) {
        return { ok: false };
      }

      if (typeof window.liquidAjaxCart?.add === 'function') {
        return new Promise((resolve) => {
          let resolved = false;
          const finalize = (result) => {
            if (resolved) return;
            resolved = true;
            resolve(result);
          };

          const handleRequestEnd = (requestEndEvent) => {
            const { requestState } = requestEndEvent.detail || {};
            if (!requestState || requestState.requestType !== 'add') return;
            document.removeEventListener('liquid-ajax-cart:request-end', handleRequestEnd);
            finalize({ ok: !!requestState.responseData?.ok, requestState });
          };

          document.addEventListener('liquid-ajax-cart:request-end', handleRequestEnd);

          window.liquidAjaxCart.add(
            { items },
            {
              lastCallback: () => {
                document.removeEventListener('liquid-ajax-cart:request-end', handleRequestEnd);
                finalize({ ok: true });
              },
            }
          );
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        return { ok: false };
      }

      return { ok: true };
    }

    async onTiloPromotionSubmit(event) {
      if (!this.isTiloPromotionTemplate) return;
      if (!this.isTiloPromotionMultiSizeActive()) return;

      const form = event.currentTarget;
      if (!(form instanceof HTMLFormElement)) return;

      const quantity = this.getCurrentQuantityValue();
      if (quantity <= 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.syncTiloPromotionSizeSelectors();

      const productData = await this.getTiloPromotionProductData();
      const variantIds = this.getTiloPromotionSelectedVariantIds(form, productData);
      if (variantIds.length !== quantity) {
        console.error('Tilo promotion could not resolve all selected size variants.', { quantity, variantIds });
        return;
      }

      const addButton = form.querySelector('button[type="submit"][name="add"]');
      if (addButton?.disabled) return;

      const formData = new FormData(form);
      const properties = {};
      formData.forEach((value, key) => {
        if (!value || typeof key !== 'string') return;
        const propertyMatch = /^properties\[(.+)]$/.exec(key);
        if (!propertyMatch) return;
        properties[propertyMatch[1]] = String(value);
      });

      const items = variantIds.map((variantId) => {
        const item = {
          id: Number(variantId),
          quantity: 1,
        };

        if (Object.keys(properties).length) {
          item.properties = properties;
        }

        return item;
      });

      const spinner = addButton?.querySelector('.loading__spinner');
      if (addButton) addButton.disabled = true;
      spinner?.classList.remove('hidden');

      const cleanup = () => {
        if (addButton) addButton.disabled = false;
        spinner?.classList.add('hidden');
      };

      try {
        const addResult = await this.addTiloPromotionItems(items);
        if (addResult?.ok) {
          document.body.classList.add('js-show-ajax-cart');
        }
      } catch (error) {
        console.error('Tilo promotion multi-add failed:', error);
      } finally {
        cleanup();
      }
    }

    renderSection(hasDifferentProductUrl, productUrl, selectedQuantity = 1) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const isInQuickAddModal = this.closest('quick-add-modal') !== null;

      fetch(`${productUrl}?option_values=${this.selectedOptionValues}&section_id=${this.dataset.section}`, {
        signal: this.abortController.signal,
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          let variant = this.getSelectedVariant(html);
          if (!variant && isInQuickAddModal) {
            variant = this.resolveQuickAddVariantFallback(productUrl);
          }
          if (hasDifferentProductUrl) {
            const productInfo = html.querySelector('product-info');
            if (isInQuickAddModal) {
              productInfo.setAttribute('data-quick-add-context', 'true');
              productInfo.setAttribute('data-update-url', 'false');
            }
            this.replaceWith(productInfo);
            productInfo.updateURL(variant?.id);
            productInfo.applyQuantityValue(selectedQuantity);
          } else {
            this.updateMedia(variant?.featured_media?.id);
            this.updateURL(variant?.id);
            this.updateVariantInputs(variant?.id);
            this.updateSourceFromDestination(html, `add-to-cart-container-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `quantity-selector-inline-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `variant-selector-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `price-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `sku-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `inventory-${this.dataset.section}`);
            
            // Apply discount pricing if applicable
            this.updateDiscountPricing(variant);
            this.updateAddToCartVisibility(variant);
            this.updatePreOrderProperty(variant);
            this.applyQuantityValue(selectedQuantity);
            this.setupVariantIdSyncOnSubmit();
            this.setupTiloPromotionMultiSize();
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return;
          } else {
            console.error(error);
          }
        });
    }

  }

  customElements.define('product-info', ProductInfo);
}